import { useMemo } from 'react';

/**
 * Paginador denso para listas largas (210 parcelas, 1000+ items en bitácora).
 *
 * Estrategia visual: siempre se ven al menos
 *   - primera y última pagina
 *   - 3 anteriores y 3 siguientes a la actual
 *   - punto medio entre la actual y la primera/ultima (atajo `salto largo`)
 * Los huecos se marcan con un `…` no clickeable.
 *
 * Diseño:
 *   - Página activa: relleno con `--btn-primary-bg` (lime Aosenuma).
 *   - Páginas inactivas: outline + `--text-mid`, hover con tinte teal.
 *   - Prev/Next: outline; deshabilitados pierden opacidad.
 *   - Tamaños 28×28 px (`size='sm'`) o 32×32 (`size='md'`, default).
 */
export interface PaginationProps {
  current: number;
  total: number;
  onChange: (page: number) => void;
  size?: 'sm' | 'md';
  /** Esconde "← Anterior" / "Siguiente →" — útil en barras laterales muy estrechas. */
  compact?: boolean;
  className?: string;
}

export function Pagination({
  current,
  total,
  onChange,
  size = 'md',
  compact = false,
  className,
}: PaginationProps) {
  const items = useMemo(() => buildItems(current, total), [current, total]);
  if (total <= 1) return null;

  const cls = `pagination pagination--${size}${compact ? ' pagination--compact' : ''}${className ? ` ${className}` : ''}`;

  return (
    <nav className={cls} aria-label="Paginación">
      {!compact && (
        <button
          type="button"
          className="pagination__nav"
          onClick={() => onChange(Math.max(1, current - 1))}
          disabled={current === 1}
          aria-label="Página anterior"
        >
          <span aria-hidden="true">←</span>
          <span>Anterior</span>
        </button>
      )}

      {compact && (
        <button
          type="button"
          className="pagination__nav pagination__nav--icon"
          onClick={() => onChange(Math.max(1, current - 1))}
          disabled={current === 1}
          aria-label="Página anterior"
        >
          ←
        </button>
      )}

      <ul className="pagination__pages">
        {items.map((it, i) =>
          it === 'gap' ? (
            <li key={`gap-${i}`} className="pagination__gap" aria-hidden="true">
              …
            </li>
          ) : (
            <li key={it}>
              <button
                type="button"
                className={`pagination__page${it === current ? ' is-active' : ''}`}
                onClick={() => onChange(it)}
                aria-current={it === current ? 'page' : undefined}
                aria-label={`Página ${it}`}
              >
                {it}
              </button>
            </li>
          ),
        )}
      </ul>

      {!compact && (
        <button
          type="button"
          className="pagination__nav"
          onClick={() => onChange(Math.min(total, current + 1))}
          disabled={current === total}
          aria-label="Página siguiente"
        >
          <span>Siguiente</span>
          <span aria-hidden="true">→</span>
        </button>
      )}

      {compact && (
        <button
          type="button"
          className="pagination__nav pagination__nav--icon"
          onClick={() => onChange(Math.min(total, current + 1))}
          disabled={current === total}
          aria-label="Página siguiente"
        >
          →
        </button>
      )}
    </nav>
  );
}

/**
 * Construye la lista visible: primera, última, current ± 3, midpoints,
 * separados por 'gap' donde haya saltos.
 *
 * Casos:
 *   total ≤ 11 → todas las páginas (no hace falta condensar)
 *   total > 11 → siempre 1, total, current ± 3, midpoint(1,current), midpoint(current,total)
 */
function buildItems(current: number, total: number): Array<number | 'gap'> {
  if (total <= 11) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const set = new Set<number>();
  set.add(1);
  set.add(total);
  for (let d = -3; d <= 3; d++) {
    const p = current + d;
    if (p >= 1 && p <= total) set.add(p);
  }
  // Punto medio entre 1 y current — solo si la separación tiene sentido (>3).
  const midLow = Math.round((1 + current) / 2);
  if (midLow > 1 && midLow < current - 2) set.add(midLow);
  const midHigh = Math.round((current + total) / 2);
  if (midHigh < total && midHigh > current + 2) set.add(midHigh);

  const sorted = [...set].sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  for (let i = 0; i < sorted.length; i++) {
    const p = sorted[i]!;
    out.push(p);
    const next = sorted[i + 1];
    if (next != null && next - p > 1) out.push('gap');
  }
  return out;
}
